import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { fmtINR } from "@/lib/format";
import { usePrefs } from "@/contexts/PrefsContext";
import { CalendarDays, ArrowRight, Sparkles } from "lucide-react";

/**
 * WhatsComingStrip — compact Home section that answers "What's coming?".
 *
 * Shows at most 5 near-term events pulled from the unified VAULT calendar.
 * Purely organisational; no financial advice, no cash-flow prediction.
 */

const CAT_TONE = {
    "Credit Card Payment":  "border-red-200 text-red-800 bg-red-50/60",
    "Subscription Renewal": "border-emerald-200 text-emerald-800 bg-emerald-50/60",
    "Insurance Renewal":    "border-blue-200 text-blue-800 bg-blue-50/60",
    "Warranty Expiry":      "border-stone-200 text-stone-800 bg-stone-50",
    "Document Expiry":      "border-stone-200 text-stone-800 bg-stone-50",
    "Recurring Payment":    "border-amber-200 text-amber-900 bg-amber-50/60",
    "Investment Review":    "border-indigo-200 text-indigo-800 bg-indigo-50/60",
    "Asset Renewal":        "border-amber-200 text-amber-900 bg-amber-50/60",
    "Custom Reminder":      "border-purple-200 text-purple-800 bg-purple-50/60",
};

export default function WhatsComingStrip() {
    const { prefs } = usePrefs();
    const hide = !!prefs?.hide_balance;
    const [events, setEvents] = useState([]);
    const [total, setTotal] = useState(0);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        api.get("/vault-calendar/events", { params: { view: "next-30" } })
            .then(r => {
                setEvents((r.data?.events || []).slice(0, 5));
                setTotal(r.data?.total || 0);
            })
            .catch(() => {})
            .finally(() => setLoaded(true));
    }, []);

    if (!loaded) return null;
    if (events.length === 0) return null;

    return (
        <section className="mb-8" data-testid="whats-coming-strip">
            <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-1.5">
                        <Sparkles size={12} /> What&rsquo;s Coming
                    </div>
                    <h2 className="text-lg font-semibold text-stone-900 tracking-tight mt-1">
                        Next 30 days · {total} obligation{total === 1 ? "" : "s"}
                    </h2>
                </div>
                <Link to="/calendar"
                      className="inline-flex items-center gap-1 text-[13px] font-medium text-stone-600 hover:text-stone-900"
                      data-testid="whats-coming-view-calendar">
                    View calendar <ArrowRight size={13} />
                </Link>
            </div>
            <div className="bg-white border border-stone-200 rounded-2xl divide-y divide-stone-100 overflow-hidden">
                {events.map(e => {
                    const tone = CAT_TONE[e.category] || "border-stone-200 text-stone-700 bg-stone-50";
                    return (
                        <Link key={e.key} to={e.module_route || "/calendar"}
                              className="flex items-center gap-3 px-4 py-3 hover:bg-stone-50 transition-colors"
                              data-testid={`whats-coming-${e.key}`}>
                            <div className="w-12 shrink-0 text-right">
                                <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
                                    {fmtDate(e.date).split(" ")[1]}
                                </div>
                                <div className="text-sm font-display leading-none text-stone-900">
                                    {fmtDate(e.date).split(" ")[0]}
                                </div>
                            </div>
                            <div className="min-w-0 flex-1">
                                <span className={`inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full border ${tone}`}>
                                    {e.category}
                                </span>
                                <div className="text-[13.5px] font-semibold text-stone-900 truncate mt-1">{e.name}</div>
                            </div>
                            {e.amount != null && (
                                <div className="text-[13px] font-semibold tabular-nums text-stone-900 shrink-0">
                                    {hide ? "•••••" : fmtINR(e.amount)}
                                </div>
                            )}
                            <div className="text-[10.5px] text-stone-500 tabular-nums shrink-0 w-14 text-right">
                                {e.days_until != null && (e.days_until === 0 ? "today" : `in ${e.days_until}d`)}
                            </div>
                            <CalendarDays size={13} className="text-stone-400 shrink-0" />
                        </Link>
                    );
                })}
            </div>
            <p className="text-[11px] text-stone-400 mt-2">
                Based on records currently stored in your VAULT. Organisational view only — not financial advice.
            </p>
        </section>
    );
}
